/**
 * Prospect 360 activity timeline loader.
 * Busy claims are owned by the request that set them and are independent of the
 * latest-result gate, so a silent post-save refresh advancing the gate cannot
 * leave pagination stuck busy.
 */

import { shouldRunProspectRefreshStep } from './adminProspectQuickFollowUpController.js';
import { shouldStartAdminProspectOwnedLoad } from './adminProspectVerificationOwnership.js';

/**
 * Pagination always tracks busy (even when visit-bound for ownership).
 * Visit-bound post-save refresh stays silent so it does not flash loading/busy.
 */
export function prospectTimelineLoadTracksBusy({ append = false, ownershipBound = false } = {}) {
  return Boolean(append || !ownershipBound);
}

export function isProspectTimelineBusyClaimHeld(busyClaimRef) {
  return Number(busyClaimRef?.current || 0) !== 0;
}

/** Clear busy only when this claim still owns it — never clears a newer claim. */
export function releaseProspectTimelineBusyClaim(busyClaimRef, claimId, { mounted = true } = {}) {
  if (!mounted) return false;
  if (claimId == null || Number(busyClaimRef?.current || 0) !== Number(claimId)) return false;
  busyClaimRef.current = 0;
  return true;
}

/**
 * Production timeline load used by AdminProspectDetailPage and regression tests.
 */
export function createAdminProspectTimelineLoad({
  getDefaultProspectId,
  getActiveProspectId,
  getActiveVisitId,
  getMounted,
  getRouteVisitId,
  timelineGate,
  busyClaimRef,
  setBusy,
  setTimelineStatus,
  setTimelineErrorKind,
  setActivities,
  setNextPageToken,
  listActivities,
  buildListRequest,
  pageSize,
  classifyError,
} = {}) {
  return async function loadTimeline({
    append = false,
    pageToken,
    ownerProspectId: ownerOverride,
    visitId: visitOverride,
  } = {}) {
    const ownerProspectId = ownerOverride ?? getDefaultProspectId?.();
    const ownershipBound = ownerOverride != null || visitOverride != null;
    const requestVisitId = visitOverride ?? getRouteVisitId?.();
    const manageBusy = prospectTimelineLoadTracksBusy({ append, ownershipBound });

    if (!ownerProspectId) return { ok: false, applied: false };

    const refreshGuard = (requestId) =>
      shouldRunProspectRefreshStep({
        ownerProspectId,
        requestVisitId,
        getActiveProspectId,
        getActiveVisitId,
        getMounted,
        requestId,
        isCurrentRequest: (rid) => timelineGate.isCurrent(rid),
      });

    if (ownershipBound && !refreshGuard(null).ok) return { ok: false, applied: false };
    if (
      !ownershipBound &&
      !shouldStartAdminProspectOwnedLoad({
        ownerProspectId,
        activeProspectId: getActiveProspectId?.(),
      })
    ) {
      return { ok: false, applied: false };
    }

    // Prevent concurrent pagination before React re-renders busy.
    if (append && isProspectTimelineBusyClaimHeld(busyClaimRef)) {
      return { ok: false, applied: false, reason: 'busy' };
    }

    const requestId = timelineGate.next();
    let busyClaimId = null;
    if (manageBusy) {
      // Claim id is this request's id; release checks claim ownership — not gate currency.
      busyClaimId = requestId;
      busyClaimRef.current = busyClaimId;
      setBusy?.(true);
      if (!append) {
        setTimelineStatus?.('loading');
        setTimelineErrorKind?.(null);
      }
    }

    try {
      const data = await listActivities(
        buildListRequest({
          prospectId: ownerProspectId,
          pageSize,
          pageToken,
        }),
      );
      if (ownershipBound) {
        if (!refreshGuard(requestId).ok) return { ok: false, applied: false };
      } else if (!getMounted?.() || !timelineGate.isCurrent(requestId)) {
        return { ok: false, applied: false };
      }
      const rows = Array.isArray(data?.activities) ? data.activities : [];
      if (!ownershipBound || refreshGuard(requestId).ok) {
        setActivities?.((current) => (append ? [...current, ...rows] : rows));
        setNextPageToken?.(data?.nextPageToken || null);
        if (append) setTimelineStatus?.('ready');
        else setTimelineStatus?.(rows.length === 0 ? 'empty' : 'ready');
      }
      return { ok: true, applied: true, data };
    } catch (err) {
      if (ownershipBound) {
        if (!refreshGuard(requestId).ok) return { ok: false, applied: false };
      } else if (!getMounted?.() || !timelineGate.isCurrent(requestId)) {
        return { ok: false, applied: false };
      }
      if (!ownershipBound || refreshGuard(requestId).ok) {
        setTimelineErrorKind?.(classifyError?.(err));
        if (!append) {
          setActivities?.([]);
          setNextPageToken?.(null);
        }
        setTimelineStatus?.('error');
      }
      return { ok: false, applied: true };
    } finally {
      if (
        manageBusy &&
        releaseProspectTimelineBusyClaim(busyClaimRef, busyClaimId, {
          mounted: Boolean(getMounted?.()),
        })
      ) {
        setBusy?.(false);
      }
    }
  };
}
